import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSetFocusedFieldProps, useUnsetFocusedFieldProps } from '../../../../actions/shortcuts';
import { checkExistValue } from '../../../../service/utils';

export const useInput = ({
    defaultValue,
    errorText,
    manual,
    name,
    onBlur,
    onEditField,
    onFocus,
    setManual,
    shortcuts,
    validate,
    value,
}) => {
    const [firstRender, setFirstRender] = useState(true);
    const [currentValue, setCurrentValue] = useState(defaultValue !== undefined && defaultValue !== null ? defaultValue : value);
    const setFocusedFieldProps = useSetFocusedFieldProps();
    const unsetFocusedFieldProps = useUnsetFocusedFieldProps();
    const { t: translate } = useTranslation();

    const changeHandler = useCallback(
        (newValue) => {
            if (newValue === 'manual') {
                setManual(true);

                return;
            }
            validate(newValue);
            setCurrentValue(newValue);
            if (onBlur) {
                onBlur(newValue);
            }
        },
        [onBlur]
    );

    const handleFocus = useCallback(
        (evt) => {
            if (!manual && shortcuts?.length) {
                setFocusedFieldProps({
                    shortcuts,
                    id: name,
                    value: evt.target.value,
                    changeHandler,
                });
            } else {
                unsetFocusedFieldProps();
            }

            if (onFocus) {
                onFocus(evt);
            }
        },
        [manual, shortcuts, name, setFocusedFieldProps, unsetFocusedFieldProps, onFocus, changeHandler]
    );

    const handleChange = useCallback(
        (evt) => {
            const newValue = evt.target.value;
            setCurrentValue(newValue);
            validate(newValue);

            if (onBlur && shortcuts && shortcuts.length && !manual) {
                onBlur(evt);
            }
        },
        [validate, onBlur, shortcuts, manual]
    );

    const handleBlur = useCallback(
        (evt) => {
            const isValid = validate(evt.target.value);

            if (isValid) {
                setCurrentValue(evt.target.value);
                if (manual || !shortcuts || !shortcuts.length) {
                    onEditField(name, evt.target.value);
                }
            }

            if (onBlur) {
                onBlur(evt.target.value);
            }

            setManual(false);
        },
        [manual, currentValue, validate, onEditField, name, onBlur, shortcuts]
    );

    useEffect(() => {
        if (checkExistValue(defaultValue)) {
            setCurrentValue(defaultValue);
            validate(defaultValue);
        }
        setFirstRender(false);
    }, []);

    useEffect(() => {
        if (value !== undefined && value !== currentValue) {
            setCurrentValue(value);
            validate(value);
        }
    }, [value]);

    useEffect(() => {
        if (!firstRender && shortcuts?.length && !errorText && !manual) {
            onEditField(name, currentValue);
        }
    }, [currentValue]);

    useEffect(() => {
        if (!firstRender && checkExistValue(defaultValue) && defaultValue !== currentValue) {
            validate(currentValue);
        }
    }, [translate]);

    return { currentValue, handleFocus, handleChange, handleBlur };
};
